import { LessonLikeButton } from "@app/grades/components";
import type { Lesson } from "@core/grades/types";
import { platformFeaturesAtom } from "@framework/shared/atoms";
import { trans } from "@mongez/localization";
import { Link } from "@remix-run/react";
import {
  ArrowLeft,
  BookOpen,
  Clock3,
  Eye,
  MessageCircle,
  Play,
  Sparkles,
  Star,
  UserRound,
} from "lucide-react";
import { useModernHomeLesson } from "../../hooks/use-modern-home-lesson.hook";

function LessonImage({
  image,
  name,
  className,
}: {
  image?: string;
  name: string;
  className: string;
}) {
  if (image) {
    return <img src={image} alt={name} loading="lazy" className={className} />;
  }

  return (
    <div
      className={`${className} flex items-center justify-center bg-gradient-to-br from-sky-100 via-indigo-100 to-violet-100 text-indigo-500 dark:from-slate-900 dark:via-indigo-950 dark:to-violet-950`}
    >
      <BookOpen className="h-16 w-16 opacity-40" />
    </div>
  );
}

function Instructor({
  image,
  name,
  inverse = false,
}: {
  image?: string;
  name?: string;
  inverse?: boolean;
}) {
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      {image ? (
        <img
          src={image}
          alt={name || ""}
          className="h-9 w-9 shrink-0 rounded-full object-cover ring-2 ring-white/70 dark:ring-slate-800"
        />
      ) : (
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
            inverse
              ? "bg-white/15 text-white"
              : "bg-slate-100 text-slate-500 dark:bg-slate-800"
          }`}
        >
          <UserRound className="h-4 w-4" />
        </span>
      )}
      <span
        className={`truncate text-xs font-bold ${
          inverse ? "text-white/90" : "text-slate-600 dark:text-slate-300"
        }`}
      >
        {name || trans("home.lessonInstructor")}
      </span>
    </div>
  );
}

function FeaturedLatestLesson({ lesson }: { lesson: Lesson }) {
  const details = useModernHomeLesson(lesson);
  const commentsEnabled =
    platformFeaturesAtom.useValue().commentsAndRatings === true;

  return (
    <article className="group relative isolate min-h-[29rem] overflow-hidden rounded-[1.9rem] border border-slate-900/10 bg-slate-950 text-white shadow-2xl shadow-indigo-950/15 lg:row-span-2">
      <Link to={details.href} className="absolute inset-0">
        <LessonImage
          image={details.image}
          name={lesson.name}
          className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-slate-950/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/20 via-transparent to-transparent rtl:bg-gradient-to-l" />
        <div className="absolute inset-x-0 top-0 flex items-start justify-between gap-3 p-5">
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-violet-300/40 bg-violet-600 px-3.5 py-2 text-[11px] font-black text-white shadow-lg shadow-violet-950/30">
              <Sparkles className="h-3.5 w-3.5" />
              {trans("home.newLesson")}
            </span>
            {details.subject ? (
              <span className="rounded-full border border-white/20 bg-slate-950/75 px-3.5 py-2 text-[11px] font-bold text-white shadow-sm backdrop-blur-md">
                {details.subject}
              </span>
            ) : null}
          </div>
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-white/50 bg-white text-slate-950 shadow-xl shadow-slate-950/30 transition group-hover:scale-110 group-hover:bg-violet-500 group-hover:text-white">
            <Play className="h-4 w-4 fill-current" />
          </span>
        </div>

        <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
          <Instructor
            image={details.instructorImage}
            name={details.instructorName}
            inverse
          />
          <h3 className="mt-4 line-clamp-3 text-2xl font-black leading-tight sm:text-3xl">
            {lesson.name}
          </h3>
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs font-bold text-white/75">
            {details.publishedAt ? <span>{details.publishedAt}</span> : null}
            {details.duration ? (
              <span className="inline-flex items-center gap-1.5" dir="ltr">
                <Clock3 className="h-3.5 w-3.5" />
                {details.duration}
              </span>
            ) : null}
            <span className="inline-flex items-center gap-1.5" dir="ltr">
              <Eye className="h-3.5 w-3.5" />
              {details.views}
            </span>
            {commentsEnabled ? (
              <span className="inline-flex items-center gap-1.5" dir="ltr">
                <MessageCircle className="h-3.5 w-3.5" />
                {details.comments}
              </span>
            ) : null}
          </div>
          <div className="mt-6 flex items-center justify-between gap-3 border-t border-white/15 pt-5">
            <strong className="rounded-full bg-white px-4 py-2 text-sm text-slate-950 shadow-lg">
              {details.price}
            </strong>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-black backdrop-blur-sm transition group-hover:bg-violet-600">
              {trans("home.openLesson")}
              <ArrowLeft className="h-4 w-4 ltr:rotate-180 rtl:rotate-0" />
            </span>
          </div>
        </div>
      </Link>
      <LessonLikeButton
        lesson={lesson}
        compact
        inverse
        className="absolute end-5 top-20 z-10"
      />
    </article>
  );
}

function LatestLessonCard({ lesson }: { lesson: Lesson }) {
  const details = useModernHomeLesson(lesson);
  const commentsEnabled =
    platformFeaturesAtom.useValue().commentsAndRatings === true;

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-[1.6rem] border border-slate-200/90 bg-white shadow-[0_8px_30px_rgba(15,23,42,0.05)] transition duration-300 hover:-translate-y-1.5 hover:border-violet-200 hover:shadow-[0_18px_45px_rgba(79,70,229,0.13)] dark:border-slate-800 dark:bg-slate-950 dark:hover:border-violet-800">
      <Link
        to={details.href}
        className="relative block aspect-[16/10] overflow-hidden bg-slate-100 dark:bg-slate-900"
      >
        <LessonImage
          image={details.image}
          name={lesson.name}
          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/55 via-transparent to-slate-950/5" />
        <span className="absolute start-3 top-3 max-w-[70%] truncate rounded-full border border-white/50 bg-white/95 px-3 py-1.5 text-[10px] font-black text-violet-700 shadow-md backdrop-blur dark:border-slate-700 dark:bg-slate-950/90 dark:text-violet-300">
          {details.subject || trans("home.newLesson")}
        </span>
        <span className="absolute end-3 top-3 flex h-9 w-9 translate-y-1 items-center justify-center rounded-full bg-slate-950/80 text-white opacity-0 shadow-lg backdrop-blur transition duration-300 group-hover:translate-y-0 group-hover:opacity-100">
          <Play className="h-3.5 w-3.5 fill-current" />
        </span>
        {details.duration ? (
          <span
            className="absolute bottom-3 end-3 inline-flex items-center gap-1 rounded-full border border-white/10 bg-slate-950/85 px-2.5 py-1.5 text-[10px] font-bold text-white shadow-sm backdrop-blur"
            dir="ltr"
          >
            <Clock3 className="h-3 w-3" />
            {details.duration}
          </span>
        ) : null}
      </Link>

      <div className="flex flex-1 flex-col p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <Instructor
            image={details.instructorImage}
            name={details.instructorName}
          />
          {details.rating > 0 ? (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-100 bg-amber-50 px-2.5 py-1 text-[10px] font-black text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
              <Star className="h-3 w-3 fill-current" />
              {details.rating}
            </span>
          ) : null}
        </div>

        <Link to={details.href}>
          <h3 className="mt-3 line-clamp-2 min-h-12 text-[15px] font-black leading-6 text-slate-950 transition group-hover:text-violet-700 dark:text-white dark:group-hover:text-violet-300">
            {lesson.name}
          </h3>
        </Link>

        <div className="mt-3 flex flex-wrap items-center gap-2 text-[10px] font-bold text-slate-500 dark:text-slate-400">
          <span
            className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1.5 dark:bg-slate-900"
            dir="ltr"
          >
            <Eye className="h-3.5 w-3.5" />
            {details.views}
          </span>
          {commentsEnabled ? (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1.5 dark:bg-slate-900"
              dir="ltr"
            >
              <MessageCircle className="h-3.5 w-3.5" />
              {details.comments}
            </span>
          ) : null}
          <LessonLikeButton lesson={lesson} compact />
          {details.category ? (
            <span className="max-w-28 truncate rounded-full bg-violet-50 px-2.5 py-1.5 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300">
              {details.category}
            </span>
          ) : null}
        </div>

        <div className="mt-4 flex items-center justify-between gap-3 border-t border-slate-100 pt-4 dark:border-slate-900">
          <div>
            {details.publishedAt ? (
              <p className="text-[10px] font-bold text-slate-400">
                {details.publishedAt}
              </p>
            ) : null}
            <strong className="mt-1 block text-sm font-black text-slate-950 dark:text-white">
              {details.price}
            </strong>
          </div>
          <Link
            to={details.href}
            aria-label={lesson.name}
            className="inline-flex h-10 items-center gap-2 rounded-xl bg-slate-950 px-3.5 text-xs font-black text-white transition group-hover:bg-violet-600 dark:bg-white dark:text-slate-950 dark:group-hover:bg-violet-300"
          >
            <span className="hidden 2xl:inline">
              {trans("home.openLesson")}
            </span>
            <ArrowLeft className="h-4 w-4 ltr:rotate-180 rtl:rotate-0" />
          </Link>
        </div>
      </div>
    </article>
  );
}

export function ModernHomeLatestLessons({
  lessons = [],
}: {
  lessons?: Lesson[];
}) {
  if (lessons.length === 0) return null;

  const featuredLesson = lessons[0];
  const lessonCards = lessons.slice(1, 7);

  return (
    <section className="relative isolate overflow-hidden rounded-[2.25rem] border border-indigo-100 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950 sm:p-7">
      <div className="pointer-events-none absolute -end-24 -top-28 h-72 w-72 rounded-full bg-indigo-300/20 blur-3xl dark:bg-indigo-700/10" />
      <div className="pointer-events-none absolute -bottom-28 -start-24 h-72 w-72 rounded-full bg-cyan-300/20 blur-3xl dark:bg-cyan-700/10" />

      <header className="relative mb-6 flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 text-white shadow-lg shadow-indigo-600/20">
            <Sparkles className="h-5 w-5" />
          </span>
          <div>
            <p className="text-xs font-black uppercase tracking-[0.16em] text-indigo-600 dark:text-indigo-300">
              {trans("home.freshLearning")}
            </p>
            <h2 className="mt-1 text-2xl font-black text-slate-950 dark:text-white sm:text-3xl">
              {trans("home.latestLessons")}
            </h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {trans("home.latestLessonsDescription")}
            </p>
          </div>
        </div>
        <Link
          to="/lessons"
          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-black text-slate-700 transition hover:border-indigo-200 hover:text-indigo-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-indigo-800 dark:hover:text-indigo-300"
        >
          {trans("home.viewAll")}
          <ArrowLeft className="h-4 w-4 ltr:rotate-180 rtl:rotate-0" />
        </Link>
      </header>

      <div className="relative grid gap-4 lg:grid-cols-2 xl:grid-cols-[minmax(0,1.08fr)_minmax(0,1.42fr)]">
        <FeaturedLatestLesson lesson={featuredLesson} />
        <div className="grid gap-4 sm:grid-cols-2">
          {lessonCards.map((lesson) => (
            <LatestLessonCard key={lesson.id} lesson={lesson} />
          ))}
        </div>
      </div>
    </section>
  );
}
